// src/pages/Admin/ReglementInscription/ReglementCharts.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const CurrencyTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-sm ${
        isDark
          ? "bg-slate-900 border-slate-700 text-slate-100"
          : "bg-white border-slate-200 text-slate-800"
      }`}
    >
      <div className="text-[11px] opacity-70">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="text-sm font-semibold">
          {fmtEUR(p.value)}
        </div>
      ))}
    </div>
  );
};

export default function ReglementCharts({ data, season }) {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  const totalFn = (r) =>
    (Number(r?.montant_lic_assurance) || 0) +
    (Number(r?.montant_adhesion) || 0) +
    (Number(r?.montant_cotisation) || 0) +
    (Number(r?.reduction_foyer) || 0) +
    (Number(r?.pass_sport) || 0) +
    (Number(r?.reduction_saison) || 0);

  // 1) Totaux par statut
  const byStatus = useMemo(() => {
    const map = new Map();
    data.forEach((r) => {
      const key = r?.statut_reglement || "—";
      map.set(key, (map.get(key) || 0) + totalFn(r));
    });
    return Array.from(map.entries())
      .map(([statut, total]) => ({ statut, total: Math.round(total) }))
      .sort((a, b) => b.total - a.total);
  }, [data]);

  // 2) Top groupes par total
  const byGroup = useMemo(() => {
    const map = new Map();
    data.forEach((r) => {
      const groups = r?.Adherent?.Groups || [];
      const t = totalFn(r);
      if (!groups.length) {
        map.set("Sans groupe", (map.get("Sans groupe") || 0) + t);
      } else {
        groups.forEach((g) => {
          const key = g?.nom || "Sans nom";
          map.set(key, (map.get(key) || 0) + t);
        });
      }
    });
    return Array.from(map.entries())
      .map(([groupe, total]) => ({ groupe, total: Math.round(total) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [data]);

  // 3) Totaux par type de règlement (Pie)
  const byType = useMemo(() => {
    const map = new Map();
    data.forEach((r) => {
      const key = r?.type_reglement || "—";
      map.set(key, (map.get(key) || 0) + totalFn(r));
    });
    return Array.from(map.entries())
      .map(([type, total]) => ({ name: type, value: Math.round(total) }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark
    ? "rgba(148,163,184,0.12)"
    : "rgba(148,163,184,0.15)";
  const topColor = isDark ? "#9CA3FF" : "#A5B4FC";
  const bottomColor = isDark ? "#6C72D9" : "#818CF8";
  const pieColors = [
    "#60A5FA",
    "#34D399",
    "#F59E0B",
    "#F472B6",
    "#A78BFA",
    "#F87171",
    "#10B981",
    "#FBBF24",
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4">
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Totaux par statut — {season || "toutes saisons"}
          </h4>
        </div>
        <div className="w-full h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={byStatus}
              margin={{ top: 20, right: 16, left: 4, bottom: 40 }}
            >
              <defs>
                <linearGradient id="barFill1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={topColor} stopOpacity={0.95} />
                  <stop
                    offset="100%"
                    stopColor={bottomColor}
                    stopOpacity={0.85}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis
                dataKey="statut"
                height={30}
                tick={{ fill: tick, fontSize: 12 }}
              />
              <YAxis
                tick={{ fill: tick, fontSize: 12 }}
                width={60}
                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              />
              <Tooltip
                cursor={{ fill: cursorFill }}
                content={<CurrencyTooltip isDark={isDark} />}
              />
              <Bar dataKey="total" fill="url(#barFill1)" radius={[8, 8, 0, 0]}>
                <LabelList
                  dataKey="total"
                  position="top"
                  formatter={(v) => fmtEUR(v)}
                  style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4">
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Top 10 groupes par total — {season || "toutes saisons"}
          </h4>
        </div>
        <div className="w-full h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byGroup} margin={{ top: 20, right: 16, left: 4, bottom: 80 }}>
              <defs>
                <linearGradient id="barFill2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#93C5FD" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.85} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis
                dataKey="groupe"
                interval={0}
                angle={-25}
                dy={20}
                tick={{ fill: tick, fontSize: 12 }}
              />
              <YAxis tick={{ fill: tick, fontSize: 12 }} width={60} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
              <Tooltip cursor={{ fill: cursorFill }} content={<CurrencyTooltip isDark={isDark} />} />
              <Bar dataKey="total" fill="url(#barFill2)" radius={[8, 8, 0, 0]}>
                <LabelList
                  dataKey="total"
                  position="top"
                  formatter={(v) => fmtEUR(v)}
                  style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div> */}

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4">
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Répartition par type de règlement — {season || "toutes saisons"}
          </h4>
        </div>
        <div className="w-full h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip content={<CurrencyTooltip isDark={isDark} />} />
              <Legend />
              <Pie
                data={byType}
                dataKey="value"
                nameKey="name"
                outerRadius={110}
                innerRadius={55}
                paddingAngle={3}
              >
                {byType.map((entry, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={pieColors[idx % pieColors.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
